import {
    AlertTriangle, CalendarDays, CalendarOff, CalendarRange, Clock, Palmtree, Percent, Scale, Stethoscope, Target, Users,
} from 'lucide-react';
import type { AbsenceSummary, HoursSummary, VacationPlanSummary } from '../../types/timerocket';
import { formatDays, formatMinutes, formatPercent } from './format';

interface Props {
    summary: HoursSummary;
    /** Sum of the vacation account's days over the rows that have a converted value; null when none has. */
    openVacationDays: number | null;
    /** Label of the vacation account as the tenant delivers it ("Ferien"); null when the catalog has none. */
    vacationLabel: string | null;
}

/**
 * The five numbers the page exists for, over the WHOLE scope as the server answered it — not
 * over the table's current column filters (the table footer is what is on screen; these tiles
 * are the period's totals). Same tile style as customer-duplicates/KpiRow so the platform reads
 * as one app.
 *
 * Balance is actual − target, NOT presence − target: a public holiday arrives from TimeRocket
 * as presence 0 with actual = target, and a presence-based balance would punish every holiday.
 */
export function KpiRow({ summary, openVacationDays, vacationLabel }: Props) {
    const balanceClass = summary.balanceMin < 0 ? 'text-red-600' : 'text-green-700';
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Tile icon={Users} label="People" value={summary.people.toLocaleString('de-CH')} />
            <Tile icon={Target} label="Target" value={formatMinutes(summary.targetMin)} hint="Sum of the daily targets that have fallen due in the period" />
            <Tile icon={Clock} label="Actual" value={formatMinutes(summary.actualMin)} hint="Booked time incl. paid absences (holidays, vacation)" />
            <Tile icon={Scale} label="Balance" value={formatMinutes(summary.balanceMin)} valueClass={balanceClass} hint="Actual − target over the period" />
            <Tile
                icon={CalendarDays}
                label={vacationLabel ? `Open vacation (${vacationLabel})` : 'Open vacation'}
                value={openVacationDays === null ? 'n/a' : formatDays(openVacationDays)}
                hint={openVacationDays === null
                    ? 'No vacation balance with a day conversion rate in this scope'
                    : 'Days, summed over the people whose balance has a conversion rate — as of the balance date, not the period'}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------------------------
// Absences
// ---------------------------------------------------------------------------------------------

interface AbsencesProps {
    summary: AbsenceSummary;
    /** From the response, decided server-side: the sick rate exists only for own / CanEdit. */
    typesVisible: boolean;
    /** Sum of the rows' target days — the summary carries the denominator in minutes only. */
    targetDays: number;
    /** Ferien / Feiertag minutes converted per person; null when no row has a conversion rate. */
    vacationDays: number | null;
    holidayDays: number | null;
}

/**
 * The absence view's totals over the whole scope. The two rates divide summed minutes by summed
 * target minutes (the server's summary), so a 100 % person and a 40 % person weigh by their
 * actual targets, not one-person-one-vote.
 *
 * For a supervisor's team scope there is NO sick-rate tile — five tiles, not six with a blank.
 * The sum of sickness + accident is health data (P3 #2), and even a placeholder reading "HR
 * only" would say "there is a figure here you may not see".
 *
 * The day sums (target, vacation, holiday) come from the page over the rows WITH daily rows —
 * the same rule as the server's summary and the table footer, so tile and footer agree.
 */
export function AbsencesKpiRow({ summary, typesVisible, targetDays, vacationDays, holidayDays }: AbsencesProps) {
    const peopleHint = summary.withRows < summary.people
        ? `${summary.people - summary.withRows} without daily rows in this period — their rates are undefined`
        : 'Every person has daily rows in this period';
    return (
        <div className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${typesVisible ? 'lg:grid-cols-6' : 'lg:grid-cols-5'}`}>
            <Tile icon={Users} label="People" value={summary.people.toLocaleString('de-CH')} hint={peopleHint} />
            {/* A count of workdays — an integer, formatted like the table shows it, not like a converted day figure. */}
            <Tile icon={Target} label="Target days" value={targetDays.toLocaleString('de-CH')} hint="Workdays with a target > 0 in the period, over the people with daily rows — the denominator of both rates" />
            <Tile
                icon={Percent} label="Absence rate" value={formatPercent(summary.absenceRate)}
                hint="Everything away except vacation, holidays and away-working, over the target minutes of the period"
            />
            {typesVisible && (
                <Tile
                    icon={Stethoscope} label="Sick rate" value={formatPercent(summary.sickRate)}
                    hint="Sickness + accident over the target minutes of the period"
                />
            )}
            <Tile
                icon={Palmtree} label="Vacation days" value={vacationDays === null ? 'n/a' : formatDays(vacationDays)}
                hint={vacationDays === null ? 'No conversion rate in this scope' : 'Vacation (Ferien) minutes converted per person, over the rows with a conversion rate and daily rows'}
            />
            <Tile
                icon={CalendarOff} label="Holiday days" value={holidayDays === null ? 'n/a' : formatDays(holidayDays)}
                hint={holidayDays === null ? 'No conversion rate in this scope' : 'Public-holiday (Feiertag) minutes converted per person, over the rows with a conversion rate and daily rows'}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------------------------
// Vacation plan
// ---------------------------------------------------------------------------------------------

/**
 * Today's balance, what is already planned to year end and the projected rest — plus how many
 * people the thresholds flag. Rest = balance − planned (P3 #3); the sums here skip people
 * without a balance, so the tile's rest is over fewer people than "People" when
 * `withoutBalance` > 0, and the hint says so.
 *
 * "≈" on the planned and rest tiles when ANY row's figure is estimated or stale: a sum inherits
 * the doubt of its least certain term, and the hint says how many people carry it.
 */
export function VacationKpiRow({ summary }: { summary: VacationPlanSummary }) {
    const peopleHint = summary.withoutBalance > 0
        ? `${summary.withoutBalance} without a synced vacation balance — not in the day sums`
        : 'Every person has a synced vacation balance';
    const approx = summary.approximate > 0;
    const approxPrefix = approx ? '≈ ' : '';
    const approxHint = approx
        ? ` — ≈ ${summary.approximate} ${summary.approximate === 1 ? 'person' : 'people'} with an estimated or stale figure`
        : '';
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Tile icon={Users} label="People" value={summary.people.toLocaleString('de-CH')} hint={peopleHint} />
            <Tile icon={CalendarDays} label="Balance days" value={formatDays(summary.balanceDays)} hint="Open vacation as of the latest balance date, summed" />
            <Tile
                icon={CalendarRange} label="Planned days" value={`${approxPrefix}${formatDays(summary.plannedDays)}`}
                hint={`Vacation entered in TimeRocket from tomorrow to 31 December, summed${approxHint}`}
            />
            <Tile
                icon={Scale} label="Projected rest" value={`${approxPrefix}${formatDays(summary.restDays)}`}
                valueClass={summary.restDays < 0 ? 'text-red-600' : 'text-gray-900'}
                hint={`Balance − planned, summed over the people with both figures${approxHint}`}
            />
            <Tile icon={AlertTriangle} label="Red" value={summary.red.toLocaleString('de-CH')} valueClass={summary.red > 0 ? 'text-red-600' : 'text-gray-900'} hint="People whose projected rest is flagged red" />
            <Tile icon={AlertTriangle} label="Yellow" value={summary.yellow.toLocaleString('de-CH')} valueClass={summary.yellow > 0 ? 'text-amber-600' : 'text-gray-900'} hint="People whose projected rest is flagged yellow" />
        </div>
    );
}

function Tile({ icon: Icon, label, value, valueClass = 'text-gray-900', hint }: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: string;
    valueClass?: string;
    hint?: string;
}) {
    return (
        <div className="rounded border border-gray-200 bg-white p-3" title={hint}>
            <div className="flex items-center gap-1.5 text-xs text-gray-500"><Icon className="h-3.5 w-3.5" /> {label}</div>
            <div className={`font-semibold tabular-nums ${valueClass}`}>{value}</div>
        </div>
    );
}
